import React from 'react';
import { motion } from 'framer-motion';
import { PlayCircle, ShieldCheck, Zap } from 'lucide-react';
import TrainingLiveShowcase, { TrainingServiceType } from '../TrainingLiveShowcase/TrainingLiveShowcase';

interface TrainingSectionProps {
  badge?: string;
  title1?: string;
  title2?: string;
  description?: string;
  serviceType?: TrainingServiceType;
}

export default function TrainingSection({
  badge = "Implementação & Sucesso Lukos",
  title1 = "Impulsione agora",
  title2 = "o seu negócio.",
  description = "Não entregamos apenas software, entregamos resultados. Nossa equipe de especialistas garante uma implementação rápida e estratégica, focada em maximizar o lucro da sua operação desde o primeiro dia.",
  serviceType = 'general'
}: TrainingSectionProps) {
  return (
    <section className="bg-[#f8fafc] py-24 lg:py-40 relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#690093]/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#54a133]/5 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          
          {/* Text Content */}
          <div className="w-full lg:w-1/2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-slate-100 mb-8"
            >
              <div className="w-2 h-2 bg-[#54a133] rounded-full animate-pulse" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-[#690093]">{badge}</span>
            </motion.div>
 
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl lg:text-6xl font-black text-[#0f172a] leading-[1.05] tracking-tight mb-8"
            >
              {title1}<br />
              <span className="text-[#690093]">{title2}</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-lg text-slate-500 mb-12 leading-relaxed"
            >
              {description}
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-2xl bg-white shadow-md border border-slate-50 flex items-center justify-center shrink-0">
                  <PlayCircle className="text-[#690093]" size={24} />
                </div>
                <div>
                  <h4 className="font-black text-slate-900 text-sm uppercase tracking-wide mb-1">Workshop de Implementação</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Veja em tempo real como nossa metodologia transforma sua operação.</p>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-2xl bg-white shadow-md border border-slate-50 flex items-center justify-center shrink-0">
                  <ShieldCheck className="text-[#54a133]" size={24} />
                </div>
                <div>
                  <h4 className="font-black text-slate-900 text-sm uppercase tracking-wide mb-1">Capacitação</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Sua equipe pronta para operar com máxima eficiência e foco em resultados.</p>
                </div>
              </motion.div>
            </div>

            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-[#690093] hover:bg-[#59007d] text-white px-10 py-5 rounded-full font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-purple-200 transition-all flex items-center gap-3"
            >
              Falar com Consultor <Zap size={16} className="fill-white" />
            </motion.button>
          </div>

          {/* Large Video Visual */}
          <div className="w-full lg:w-1/2">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', damping: 20 }}
            >
              <TrainingLiveShowcase serviceType={serviceType} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
